import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react'
import { ZodType } from 'zod/v4'
import Environment from '@/constants/environment'
import env from '@/utils/env'
import logger from '@/utils/logger'

type ZodBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  { dataSchema?: ZodType },
  FetchBaseQueryMeta
>

const zodBaseQueryWrapper: (baseQuery: ZodBaseQuery) => ZodBaseQuery =
  (baseQuery: ZodBaseQuery) => async (args, api, extraOptions) => {
    const returnValue = await baseQuery(args, api, extraOptions)

    if ('error' in returnValue && returnValue.error) {
      return returnValue
    }

    const zodSchema = extraOptions?.dataSchema
    const { data } = returnValue

    if (!data || !zodSchema) {
      return returnValue
    }

    const parseResult = zodSchema.safeParse(data)

    if (!parseResult.success) {
      logger.error(
        parseResult.error,
        {
          endpoint: api.endpoint,
          data: JSON.stringify(data),
          args: JSON.stringify(args),
          issues: parseResult.error.issues,
        },
        { 'zod-parse-failure': true },
      )

      if (env.ENVIRONMENT !== Environment.Production) {
        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: parseResult.error.message,
          },
        }
      }
    } else {
      return {
        ...returnValue,
        data: parseResult.data,
      }
    }

    return returnValue
  }

export default zodBaseQueryWrapper
